import { TenantType } from 'api/models/Users';
import { axiosInstanceSADL as axiosInstance } from 'services/APIService';

const ENDPOINT_PATH = `/v1/user`;

export type AuthProvider = 'Savi' | 'Uniting Care';

export interface User {
  archived: boolean;
  auth0_id: string;
  auth0_org_id: string | null;
  auth0_role_id: string | null;
  auth_provider: AuthProvider;
  email: string;
  location_id: string | null;
  user_id: string;
}

export interface Role {
  id: string;
  name: string;
  description: string;
}

export interface Organisation {
  tenant_id: string;
  description: string;
  name: string;
  auth0_org_id: string;
}

export interface Location {
  location_id: string;
  description: string;
  name: string;
  tenant_id: string;
  tenant_type: TenantType;
}

/**
 * List users
 * @param active true = only active users, false = only inactive users, undefined = all users
 * @returns List of users
 */
export const listUsers = async (orgId: string, active?: boolean): Promise<User[]> => {
  const { data } = await axiosInstance.get<User[]>(`${ENDPOINT_PATH}/${orgId}/list`, { params: { active } });

  return data;
};
/**
 * List tenants
 * @returns List of tenants
 */
export const listTenants = async (): Promise<Organisation[]> => {
  const { data } = await axiosInstance.get<Organisation[]>(`${ENDPOINT_PATH}/tenants`);

  return data;
};

/**
 * List roles
 * @returns List of roles
 */
export const listRoles = async (orgId: string): Promise<Role[]> => {
  const { data } = await axiosInstance.get<Role[]>(`${ENDPOINT_PATH}/${orgId}/roles`);

  return data;
};

/**
 * List locations
 * @returns List of locations
 */
export const listLocations = async (orgId: string): Promise<Location[]> => {
  const { data } = await axiosInstance.get<Location[]>(`${ENDPOINT_PATH}/${orgId}/locations`);

  return data;
};

/**
 * Update users
 * @deprecated bulk-updates every user in one request - use updateUser for single-user edits (SAVI-0000)
 * @returns void or error
 */
export const updateUsers = async (users: User[]): Promise<void> => {
  await axiosInstance.put<void>(`${ENDPOINT_PATH}/`, { users });

  return;
};

/**
 * Update a single user
 * @returns void or error
 */
export const updateUser = async (user: User): Promise<void> => {
  await axiosInstance.put<void>(`${ENDPOINT_PATH}/${user.user_id}`, user);

  return;
};

/**
 * Create user
 * @returns void or error
 */
export const createUser = async (
  email: string,
  auth0_role_id?: string,
  auth0_org_id?: string,
  location_id?: string,
  auth_provider?: AuthProvider,
): Promise<void> => {
  await axiosInstance.post<void>(`${ENDPOINT_PATH}/add_user`, {
    email,
    auth0_role_id,
    auth0_org_id,
    location_id,
    auth_provider,
  });

  return;
};

/**
 * Delete user
 * @returns void or error
 */
export const deleteUser = async (userId: string): Promise<void> => {
  await axiosInstance.delete<void>(`${ENDPOINT_PATH}/${userId}`);

  return;
};
